/**
 * The devices the preview's menu names (decision 11; part 15c): each under its brand, and the device profile it shares.
 * A name is what a writer looks for; the profile is what is laid out, so devices with similar screens show the same pages.
 */
import type { ProfileId } from '@cockatrice/layout';
import type { Messages } from '../i18n/en.ts';

/** The menu's groups, in its order. */
export const BRANDS = Object.freeze(['general', 'kindle', 'kobo', 'apple', 'android', 'eink'] as const);

/** A group of the menu. */
export type Brand = (typeof BRANDS)[number];

/** A device the writer can pick. */
export interface Device {
  readonly id: string;
  readonly brand: Brand;
  /** Its name in the catalogue. */
  readonly name: keyof Messages & `preview.device.${string}`;
  /** The device profile it shares (decision 11). */
  readonly profile: ProfileId;
}

function device<I extends string>(id: I, brand: Brand, profile: ProfileId): { readonly id: I; readonly brand: Brand; readonly name: `preview.device.${I}`; readonly profile: ProfileId } {
  return Object.freeze({ id, brand, name: `preview.device.${id}` as const, profile });
}

/** Every device, in the menu's order. */
export const DEVICES: readonly Device[] = Object.freeze([
  device('general-6in', 'general', 'generic-6in'),
  device('general-a5', 'general', 'pdf-a5'),
  device('kindle', 'kindle', 'generic-6in'),
  device('kindle-paperwhite', 'kindle', 'kindle'),
  device('kindle-colorsoft', 'kindle', 'kindle'),
  device('kindle-scribe', 'kindle', 'eink-10in'),
  device('kobo-clara', 'kobo', 'generic-6in'),
  device('kobo-libra', 'kobo', 'kindle'),
  device('kobo-sage', 'kobo', 'eink-8in'),
  device('kobo-elipsa', 'kobo', 'eink-10in'),
  device('ipad', 'apple', 'tablet'),
  device('iphone', 'apple', 'phone'),
  device('galaxy-tab', 'android', 'tablet'),
  device('galaxy-phone', 'android', 'phone'),
  device('pixel-phone', 'android', 'phone'),
  device('android-tablet', 'android', 'tablet'),
  device('android-phone', 'android', 'phone'),
  device('onyx-boox', 'eink', 'eink-10in'),
  device('pocketbook', 'eink', 'generic-6in'),
  device('remarkable', 'eink', 'eink-10in'),
  device('bigme', 'eink', 'eink-8in'),
  device('tolino', 'eink', 'generic-6in'),
  device('nook', 'eink', 'generic-6in'),
]);

/** DV4, DV5: the device `id` names, as long as it shares `profile`, which is what is laid out; else null. */
export function deviceOn(id: unknown, profile: ProfileId): Device | null {
  return DEVICES.find((each) => each.id === id && each.profile === profile) ?? null;
}

/** DV7: the devices `value` names, once each, in the menu's order; nothing a build doesn't know. */
export function knownDevices(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return Object.freeze([]);
  const named: readonly unknown[] = value;
  return Object.freeze(DEVICES.filter((each) => named.includes(each.id)).map((each) => each.id));
}
